import React, { useState } from 'react'
import { Field, Form, Formik } from 'formik'
import * as Yup from 'yup'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import HeadTitle from '../../components/text/head-title'
import InputLabel from '../../components/text/input-label'
import CustomButtonComponent from '../../components/buttons/customButtonComponent/CustomButtonComponent'
import InternationalizedFormattedText from '../../components/util/internationalized-formatted-text'
import { getRedirectUri, getMarcaComercial } from '../../utils/utils'
import { requestPasswordResetService } from '../../services/services'
import RecaptchaInfo from '../../components/recaptcha/recaptcha-info'
import { RECAPTCHA_ACTION_PASSWORD_RECOVERY } from './constants'

const PasswordRecoveryPage = ({ classes }) => {
  const intl = useIntl()
  const { executeRecaptcha } = useGoogleReCaptcha()
  const brand = useSelector((state) => state.reducer.brand)
  const language = useSelector((state) => state.reducer.language)
  const redirectUri = getRedirectUri(brand?.abbreviation)
  const [requestSent, setRequestSent] = useState(false)
  const [isRequesting, setIsRequesting] = useState(false)

  const handleGoBack = (url) => {
    window.location.replace(url)
  }

  const requestPasswordResetCallback = () => {
    setRequestSent(true)
    setIsRequesting(false)
  }

  const requestPasswordResetErrorCallback = () => {
    setIsRequesting(false)
  }

  const handleSubmit = (values) => {
    // El script de reCAPTCHA se carga de forma asincrona: sin el no se puede enviar la peticion
    if (!executeRecaptcha) return

    setIsRequesting(true)
    executeRecaptcha(RECAPTCHA_ACTION_PASSWORD_RECOVERY)
      .then((token) => {
        const body = {
          login: values.login,
          idioma: language,
          captchaToken: token,
          redirectUrl: redirectUri,
        }
        requestPasswordResetService(
          getMarcaComercial(brand),
          body,
          requestPasswordResetCallback,
          requestPasswordResetErrorCallback
        )
      })
      .catch(() => {
        setIsRequesting(false)
      })
  }

  const validationSchema = Yup.object().shape({
    login: Yup.string().required(
      <InternationalizedFormattedText variant='body2' id='passwordRecovery.LoginRequired' />
    ),
  })

  return (
    <div className={`${classes.pageContainer} ${classes.centeredPageContainer}`}>
      <div className={classes.loginWrapper}>
        <HeadTitle text='passwordRecovery.title' />
        {!requestSent ? (
          <Formik
            initialValues={{ login: '' }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, touched }) => (
              <Form>
                <div className={classes.gapContainer}>
                  <div className={classes.marginBottomForm}>
                    <InputLabel text='passwordRecovery.login' />
                    <Field
                      id='login'
                      name='login'
                      type='text'
                      maxLength='128'
                      className={
                        errors.login && touched.login
                          ? 'form-control is-invalid'
                          : 'form-control'
                      }
                      placeholder={intl.formatMessage({
                        id: 'passwordRecovery.loginPlaceholder',
                      })}
                    />
                    {errors.login && touched.login && (
                      <div className='invalid-feedback'>{errors.login}</div>
                    )}
                  </div>
                  <div
                    className={`${classes.ButtonContainer} ${classes.recoveryPasswordButtons}`}
                  >
                    <CustomButtonComponent
                      type='submit'
                      label={
                        isRequesting
                          ? 'passwordRecovery.processing'
                          : 'passwordRecovery.continue'
                      }
                      disabled={isRequesting}
                      loading={isRequesting}
                      loadingText='passwordRecovery.processing'
                    />
                    <CustomButtonComponent
                      primary={false}
                      type='button'
                      label='passwordRecovery.goBack'
                      onClick={() => handleGoBack(redirectUri)}
                    />
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        ) : (
          <div className={classes.gapContainer}>
            <CheckCircleIcon className={classes.recoverySuccessIcon} />
            <InternationalizedFormattedText
              variant='body2'
              id='passwordRecovery.confirmationMessage'
            />
            <div
              className={`${classes.ButtonContainer} ${classes.recoveryPasswordButtons}`}
            >
              <CustomButtonComponent
                type='button'
                label='accept'
                onClick={() => handleGoBack(redirectUri)}
              />
            </div>
          </div>
        )}
        <RecaptchaInfo />
      </div>
    </div>
  )
}

export default PasswordRecoveryPage
